import {
  Briefcase,
  CheckCircle2,
  Filter,
  Plus,
  Smartphone,
  Tags,
  Upload,
  UserPlus,
  X,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError } from "@/api/client";
import {
  useClients,
  useCreateJob,
  useCurrentUser,
  useJobTemplates,
  useJobs,
  useOrganization,
  useOrganizations,
  usePricingRules,
  useUsers,
} from "@/api/hooks";
import { HelpPopover } from "@/components/HelpPopover";
import { StatusBadge } from "@/components/StatusBadge";
import { formatTime, shortId } from "@/lib/format";

const JOB_STATUSES = ["open", "quoted", "won", "lost"] as const;

export function JobsPage() {
  const me = useCurrentUser();
  const [view, setView] = useState<"active" | "archived">("active");
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
  const [hasWalkthrough, setHasWalkthrough] = useState<"any" | "yes" | "no">("any");
  const jobsQuery = useJobs(undefined, view, {
    statuses: Array.from(statusFilter),
    hasWalkthrough: hasWalkthrough === "any" ? undefined : hasWalkthrough === "yes",
  });
  const [creating, setCreating] = useState(false);
  const isPencilOutAdmin = me.data?.role === "pencilout_admin";

  const filterCount = statusFilter.size + (hasWalkthrough === "any" ? 0 : 1);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-1.5">
            <h1 className="font-mono text-2xl font-bold tracking-tight">Jobs</h1>
            <HelpPopover title="What a job is">
              <p>
                A job is one project for one client. Walkthroughs and quotes attach to a job —
                typically one walkthrough per job, then one quote (occasionally a re-run).
              </p>
              <p>
                Statuses: <strong>open</strong> (you're building / quoting),
                <strong> quoted</strong> (sent to client), <strong>won</strong>/{" "}
                <strong>lost</strong> (decided), <strong>in_progress</strong>
                (you're working), <strong>complete</strong> (done).
              </p>
            </HelpPopover>
          </div>
          <p className="mt-1 text-sm text-graphite-soft">
            Each job is a remodel the contractor walks through. Walkthroughs and the draft quote
            belong to one job.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Active / Archived toggle. Compact pill so it doesn't
              dominate the header. */}
          <div className="inline-flex overflow-hidden rounded-md border border-rule bg-paper-warm font-mono text-xs">
            <button
              type="button"
              onClick={() => setView("active")}
              className={
                view === "active"
                  ? "bg-graphite px-3 py-1.5 font-semibold text-paper"
                  : "px-3 py-1.5 text-graphite-soft hover:text-graphite"
              }
            >
              Active
            </button>
            <button
              type="button"
              onClick={() => setView("archived")}
              className={
                view === "archived"
                  ? "bg-graphite px-3 py-1.5 font-semibold text-paper"
                  : "px-3 py-1.5 text-graphite-soft hover:text-graphite"
              }
            >
              Archived
            </button>
          </div>
          {!creating && view === "active" && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="inline-flex items-center gap-1.5 rounded bg-graphite px-3 py-2 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden /> New job
            </button>
          )}
        </div>
      </div>

      {creating && (
        <CreateJobForm onClose={() => setCreating(false)} isPencilOutAdmin={isPencilOutAdmin} />
      )}

      {/* Filter bar — only when viewing active jobs. Archived
          view is already a narrow scope; filtering on top of it
          adds noise. */}
      {view === "active" && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-rule bg-paper-warm px-3 py-2">
          <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-graphite-soft">
            <Filter className="h-3 w-3" aria-hidden />
            Filter
          </span>
          {JOB_STATUSES.map((s) => {
            const active = statusFilter.has(s);
            return (
              <button
                key={s}
                type="button"
                onClick={() => {
                  setStatusFilter((prev) => {
                    const next = new Set(prev);
                    if (active) next.delete(s);
                    else next.add(s);
                    return next;
                  });
                }}
                className={
                  active
                    ? "rounded-full bg-graphite px-2.5 py-0.5 font-mono text-xs font-semibold text-paper"
                    : "rounded-full border border-rule bg-paper px-2.5 py-0.5 font-mono text-xs text-graphite-soft hover:text-graphite"
                }
              >
                {s}
              </button>
            );
          })}
          <span className="ml-2 font-mono text-[11px] text-graphite-faint">walkthrough:</span>
          {(["any", "yes", "no"] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setHasWalkthrough(opt)}
              className={
                hasWalkthrough === opt
                  ? "rounded-full bg-graphite px-2.5 py-0.5 font-mono text-xs font-semibold text-paper"
                  : "rounded-full border border-rule bg-paper px-2.5 py-0.5 font-mono text-xs text-graphite-soft hover:text-graphite"
              }
            >
              {opt}
            </button>
          ))}
          {filterCount > 0 && (
            <button
              type="button"
              onClick={() => {
                setStatusFilter(new Set());
                setHasWalkthrough("any");
              }}
              className="ml-auto inline-flex items-center gap-1 font-mono text-[11px] text-graphite-soft hover:text-graphite"
            >
              <X className="h-3 w-3" aria-hidden />
              Clear ({filterCount})
            </button>
          )}
        </div>
      )}

      {jobsQuery.isLoading && <p className="text-sm text-graphite-soft">Loading jobs…</p>}

      {/* Hard error only when there's nothing cached to fall back to. */}
      {jobsQuery.error && !jobsQuery.data && (
        <p className="text-sm text-red-pencil">
          {jobsQuery.error instanceof ApiError ? jobsQuery.error.detail : "Could not load jobs."}
        </p>
      )}

      {jobsQuery.data && jobsQuery.data.length === 0 && !isPencilOutAdmin && (
        <OnboardingChecklist
          orgId={me.data?.organization_id ?? null}
          onCreateJob={() => setCreating(true)}
        />
      )}
      {jobsQuery.data && jobsQuery.data.length === 0 && isPencilOutAdmin && (
        <div className="rounded-lg border border-rule bg-paper-warm p-12 text-center">
          <Briefcase className="mx-auto h-8 w-8 text-graphite-faint" aria-hidden />
          <p className="mt-4 text-graphite-soft">No jobs across any org yet.</p>
        </div>
      )}

      {jobsQuery.data && jobsQuery.data.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-rule bg-paper-warm">
          <table className="w-full">
            <thead className="border-b border-rule bg-paper">
              <tr>
                <th className="label-mono px-4 py-2 text-left">Title</th>
                <th className="label-mono px-4 py-2 text-left">Client</th>
                {isPencilOutAdmin && (
                  <th className="label-mono px-4 py-2 text-left">Organization</th>
                )}
                <th className="label-mono px-4 py-2 text-left">Created by</th>
                <th className="label-mono px-4 py-2 text-left">Status</th>
                <th className="label-mono px-4 py-2 text-right">Walkthroughs</th>
                <th className="label-mono px-4 py-2 text-right">Updated</th>
                <th className="label-mono px-4 py-2 text-right">ID</th>
              </tr>
            </thead>
            <tbody>
              {jobsQuery.data.map((job) => (
                <tr
                  key={job.id}
                  className="border-b border-rule last:border-0 hover:bg-pencil-yellow-soft"
                >
                  <td className="px-4 py-3">
                    <Link
                      to={`/jobs/${job.id}`}
                      className="font-medium text-graphite hover:text-ink hover:underline"
                    >
                      {job.title}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-graphite-soft">
                    {job.client_name ?? <span className="text-graphite-faint">—</span>}
                  </td>
                  {isPencilOutAdmin && (
                    <td className="px-4 py-3 text-graphite-soft">{job.organization_name}</td>
                  )}
                  <td className="px-4 py-3 text-graphite-soft">{job.creator_display_name}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={job.status} />
                  </td>
                  <td className="px-4 py-3 text-right font-mono tabular-nums">
                    {job.walkthrough_count}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-graphite-soft tabular-nums">
                    {formatTime(job.updated_at)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-graphite-faint">
                    {shortId(job.id)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CreateJobForm({
  onClose,
  isPencilOutAdmin,
}: {
  onClose: () => void;
  isPencilOutAdmin: boolean;
}) {
  const orgsQuery = useOrganizations();
  // For PencilOut admin: load users for the selected org once they pick.
  // For org users: backend implicitly uses their own org; no user picker
  // because creator defaults to caller server-side.
  const [orgId, setOrgId] = useState("");
  const usersQuery = useUsers(orgId || null);
  // Clients scope by org. PencilOut admin: scoped to picked org. Org
  // users: scoped to their own (backend ignores any orgId param for them).
  const clientsOrgId = isPencilOutAdmin ? orgId || null : null;
  const clientsQuery = useClients(clientsOrgId);
  // Templates scope by caller's org (server-side filter); pencilout
  // admin sees all so we just don't filter client-side here. The
  // picker is hidden when no templates exist so it's quiet for
  // contractors who haven't built any yet.
  const templatesQuery = useJobTemplates();
  const create = useCreateJob();
  const [creatorId, setCreatorId] = useState("");
  const [clientId, setClientId] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [templateId, setTemplateId] = useState("");

  // When the user picks a template, pre-fill title + description.
  // Don't clobber what they've already typed: only seed if the field
  // is empty (the contractor can clear it manually if they want
  // template-fresh inputs).
  function applyTemplate(id: string) {
    setTemplateId(id);
    if (!id) return;
    const tpl = templatesQuery.data?.find((t) => t.id === id);
    if (!tpl) return;
    if (!title.trim()) {
      setTitle(tpl.default_title || tpl.name);
    }
    if (!description.trim() && tpl.default_description) {
      setDescription(tpl.default_description);
    }
  }

  // Don't crash for org users — useOrganizations 403s on their accounts.
  const orgsAvailable = orgsQuery.data && !orgsQuery.error;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!title.trim()) return;
    if (isPencilOutAdmin && (!orgId || !creatorId)) return;
    try {
      await create.mutateAsync({
        title: title.trim(),
        description: description.trim() || null,
        client_id: clientId || undefined,
        organization_id: isPencilOutAdmin ? orgId : undefined,
        creator_id: isPencilOutAdmin ? creatorId : undefined,
        // Pass through so the server records source_template_id on
        // the new Job and seeds line items into the first quote.
        template_id: templateId || undefined,
      });
      onClose();
    } catch {
      /* error surfaces via create.error below */
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-lg border border-rule bg-paper-warm p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-mono text-sm font-semibold uppercase tracking-wider">New job</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-graphite-soft hover:text-graphite"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {isPencilOutAdmin && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="block">
            <span className="label-mono">Organization</span>
            <select
              value={orgId}
              onChange={(e) => {
                setOrgId(e.target.value);
                setCreatorId("");
              }}
              required
              className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite focus:border-graphite focus:outline-none"
            >
              <option value="">— select an organization —</option>
              {orgsAvailable &&
                orgsQuery.data?.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
            </select>
            {orgsAvailable && orgsQuery.data?.length === 0 && (
              <span className="mt-1 block text-xs text-burnt">
                No organizations yet. Create one first.
              </span>
            )}
          </label>

          <label className="block">
            <span className="label-mono">Created by (org user)</span>
            <select
              value={creatorId}
              onChange={(e) => setCreatorId(e.target.value)}
              required
              disabled={!orgId}
              className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite focus:border-graphite focus:outline-none disabled:bg-paper-warm disabled:opacity-50"
            >
              <option value="">{orgId ? "— select a user —" : "pick an organization first"}</option>
              {usersQuery.data?.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.display_name} ({u.email})
                </option>
              ))}
            </select>
            {orgId && usersQuery.data && usersQuery.data.length === 0 && (
              <span className="mt-1 block text-xs text-burnt">
                This organization has no users. Invite one from /users first.
              </span>
            )}
          </label>
        </div>
      )}

      {templatesQuery.data && templatesQuery.data.length > 0 && (
        <label className="block">
          <span className="label-mono">Start from template (optional)</span>
          <select
            value={templateId}
            onChange={(e) => applyTemplate(e.target.value)}
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite focus:border-graphite focus:outline-none"
          >
            <option value="">— blank —</option>
            {templatesQuery.data.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <span className="mt-1 block font-mono text-[11px] text-graphite-faint">
            Pre-fills title + description. The template's saved line items seed into the first blank
            quote you create on this job.
          </span>
        </label>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <label className="block md:col-span-2">
          <span className="label-mono">Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Smith kitchen remodel"
            required
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
          />
        </label>

        <label className="block md:col-span-2">
          <span className="label-mono">Client (optional)</span>
          <select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            disabled={isPencilOutAdmin && !orgId}
            className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite focus:border-graphite focus:outline-none disabled:bg-paper-warm disabled:opacity-50"
          >
            <option value="">— no client linked —</option>
            {clientsQuery.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.email ? ` (${c.email})` : ""}
              </option>
            ))}
          </select>
          {clientsQuery.data &&
            clientsQuery.data.length === 0 &&
            (isPencilOutAdmin ? orgId : true) && (
              <span className="mt-1 block text-xs text-graphite-soft">
                No clients in this org yet — add one on the Clients page first, or leave blank for a
                draft job.
              </span>
            )}
        </label>
      </div>

      <label className="block">
        <span className="label-mono">Description (optional)</span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="Full kitchen, island, custom cabinets…"
          className="mt-1 w-full rounded border border-rule bg-paper px-2 py-1.5 text-sm text-graphite placeholder:text-graphite-faint focus:border-graphite focus:outline-none"
        />
      </label>

      {create.error && (
        <p className="text-sm text-red-pencil">
          {create.error instanceof ApiError ? create.error.detail : "Could not create job."}
        </p>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onClose}
          className="rounded px-3 py-1.5 font-mono text-sm text-graphite-soft hover:text-graphite"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={
            create.isPending || !title.trim() || (isPencilOutAdmin && (!orgId || !creatorId))
          }
          className="rounded bg-graphite px-3 py-1.5 font-mono text-sm font-semibold text-paper transition hover:bg-graphite-soft disabled:opacity-50"
        >
          {create.isPending ? "Creating…" : "Create job"}
        </button>
      </div>
    </form>
  );
}

/** First-run checklist for fresh contractor accounts. Replaces the
 *  bare "no jobs yet" empty state with concrete next actions. Hides
 *  itself once any of the prerequisite states are satisfied — e.g.
 *  if the contractor already has a client, that step shows a check.
 *  When all four are done they'll have a job in the list and won't
 *  see this anymore. */
function OnboardingChecklist({
  orgId,
  onCreateJob,
}: {
  orgId: string | null;
  onCreateJob: () => void;
}) {
  const clients = useClients(orgId);
  const orgQuery = useOrganization(orgId ?? "");
  const rules = usePricingRules();
  const hasClients = (clients.data?.length ?? 0) > 0;
  const hasLogo = orgQuery.data?.has_logo ?? false;
  // Heuristic: an org with the seeded default count exactly hasn't
  // tuned anything yet. Below the seed count = they edited (deleted
  // some); above = they added. Either way "tuned" = not the default
  // shape. Seed count is 34 today (services/pricing_rule_seeds.py);
  // we use a "differs from default" heuristic rather than a hard
  // count check so the seed list can grow without breaking this.
  const ruleCount = rules.data?.length ?? 0;
  const tunedRules = ruleCount > 0 && ruleCount !== 34;

  return (
    <div className="space-y-4 rounded-lg border border-rule bg-paper-warm p-6">
      <div>
        <h2 className="font-mono text-lg font-bold tracking-tight">Welcome to PencilOut</h2>
        <p className="mt-1 text-sm text-graphite-soft">
          A few things to set up before your first walkthrough. We seeded ~30 default pricing rules
          at signup so the AI has something to work with — tune them to your real numbers before
          sending anything client-facing.
        </p>
      </div>

      <ol className="space-y-3">
        <ChecklistItem
          icon={UserPlus}
          done={hasClients}
          title="Add your first client"
          body="The homeowner whose project you'll quote. Walkthroughs attach to a client's job."
          cta={{ label: "Add client", to: "/clients" }}
        />
        <ChecklistItem
          icon={Tags}
          done={tunedRules}
          title="Tune your pricing rules"
          body="Defaults are residential-remodel mid-tier averages. Open Pricing rules and adjust the ones that don't match your market — labor rates especially. Each rule covers one category at one tier."
          cta={{
            label: tunedRules ? "Review pricing rules" : "Tune pricing rules",
            to: "/pricing-rules",
          }}
        />
        <ChecklistItem
          icon={Briefcase}
          done={false}
          title="Create a job"
          body="One job per remodel. You'll attach walkthroughs and a draft quote to it."
          cta={{ label: "New job", onClick: onCreateJob }}
        />
        <ChecklistItem
          icon={Smartphone}
          done={false}
          title="Record a walkthrough on your phone"
          body="Open the PencilOut iOS app, pick the job, hit record. Narrate what you'd say to the homeowner — the AI scopes the quote from your audio + video."
        />
        <ChecklistItem
          icon={Upload}
          done={hasLogo}
          title="Upload your logo (optional)"
          body="Customer-facing PDF quotes get your branding instead of plain PencilOut. Pro and Crew tiers — Free shows a watermark."
          cta={{ label: hasLogo ? "Replace logo" : "Upload logo", to: "/settings" }}
        />
      </ol>
    </div>
  );
}

function ChecklistItem({
  icon: Icon,
  done,
  title,
  body,
  cta,
}: {
  icon: typeof Briefcase;
  done: boolean;
  title: string;
  body: string;
  cta?: { label: string; to?: string; onClick?: () => void };
}) {
  return (
    <li className="flex gap-4">
      <span
        className={`mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full ${
          done ? "bg-graphite text-paper" : "border border-rule bg-paper text-graphite-soft"
        }`}
      >
        {done ? (
          <CheckCircle2 className="h-4 w-4" aria-hidden />
        ) : (
          <Icon className="h-3.5 w-3.5" aria-hidden />
        )}
      </span>
      <div className="flex-1">
        <p className={`font-semibold ${done ? "text-graphite-soft line-through" : ""}`}>{title}</p>
        <p className="mt-0.5 text-sm text-graphite-soft">{body}</p>
        {cta &&
          !done &&
          (cta.to ? (
            <Link
              to={cta.to}
              className="mt-2 inline-block rounded border border-graphite bg-paper px-3 py-1 font-mono text-xs font-semibold text-graphite transition hover:bg-graphite hover:text-paper"
            >
              {cta.label}
            </Link>
          ) : (
            <button
              type="button"
              onClick={cta.onClick}
              className="mt-2 inline-block rounded border border-graphite bg-paper px-3 py-1 font-mono text-xs font-semibold text-graphite transition hover:bg-graphite hover:text-paper"
            >
              {cta.label}
            </button>
          ))}
      </div>
    </li>
  );
}
